"use client";

import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FunnelCreativesHeader,
  CreativeCard,
  CreativesGridSkeleton,
  CreateAdWithIAModal,
  ViewCreativeModal,
} from "@/feature/ads/cretives";
import { creativesService } from "@/feature/ads/cretives/services/creatives.service";
import type { CreativeItem, ProductInfoCreative } from "@/feature/ads/cretives/types/creatives.types";
import { useAuth } from "@/context/AuthContext";
import { Language } from "@/utils/dictionary";
import { truncateProductName } from "@/feature/ads/cretives/utils/truncateProductName";
import HyphensTabContent from "@/components/narratives/HyphensTabContent";
import useCreativeGenerationStore from "@/store/creativeGenerationStore";
import { GeneratingPlaceholderCard } from "@/feature/ads/cretives/components/GeneratingPlaceholderCard";

type AdsProductTab = "images" | "scripts";

export default function AdsProductPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { language } = useAuth();

  const [activeTab, setActiveTab] = useState<AdsProductTab>("images");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewModalIndex, setViewModalIndex] = useState(0);
  const [viewModalEditMode, setViewModalEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<CreativeItem[]>([]);
  const [product, setProduct] = useState<ProductInfoCreative | null>(null);

  const generatingItems = useCreativeGenerationStore((s) => s.items);
  const clearCompleted = useCreativeGenerationStore((s) => s.clearCompleted);
  const prevDoneIdsRef = useRef<Set<string>>(new Set());
  const hasLoadedOnce = useRef(false);

  useEffect(() => {
    if (!productId) return;

    const loadProduct = async () => {
      try {
        const products = await creativesService.listProducts();
        const found = products.find((p) => p.product_id === productId) ?? null;
        setProduct(found);
      } catch (error) {
        console.error("Error loading product:", error);
      }
    };

    loadProduct();
  }, [productId]);

  useEffect(() => {
    if (!productId || activeTab !== "images") return;

    const loadCreatives = async () => {
      if (!hasLoadedOnce.current) {
        setLoading(true);
      }
      try {
        const data = await creativesService.listByProductId(productId);
        setItems(data.filter((item) => !!item.image_url));
      } catch (error) {
        console.error("Error loading creatives:", error);
        setItems([]);
      } finally {
        setLoading(false);
        hasLoadedOnce.current = true;
      }
    };

    loadCreatives();
  }, [productId, activeTab]);

  // Re-fetch each time an individual item finishes, then clean up done items
  useEffect(() => {
    if (activeTab !== "images" || !productId) return;

    const doneItems = generatingItems.filter((i) => i.status === "done");
    const newlyDone = doneItems.filter((i) => !prevDoneIdsRef.current.has(i.id));

    if (newlyDone.length === 0) return;

    newlyDone.forEach((i) => prevDoneIdsRef.current.add(i.id));

    const reload = async () => {
      try {
        const data = await creativesService.listByProductId(productId);
        setItems(data.filter((item) => !!item.image_url));
      } catch (error) {
        console.error("Error reloading creatives:", error);
      }

      setTimeout(() => {
        clearCompleted();
        const remaining = useCreativeGenerationStore.getState().items;
        if (remaining.length === 0) {
          prevDoneIdsRef.current.clear();
        }
      }, 1500);
    };

    reload();
  }, [generatingItems, activeTab, productId, clearCompleted]);

  const handleViewCreative = (index: number) => {
    setViewModalIndex(index - generatingItems.length);
    setViewModalEditMode(false);
    setIsViewModalOpen(true);
  };

  const handleEditCreative = (index: number) => {
    setViewModalIndex(index - generatingItems.length);
    setViewModalEditMode(true);
    setIsViewModalOpen(true);
  };

  const renderCreativesContent = () => {
    if (loading && generatingItems.length === 0) {
      return <CreativesGridSkeleton />;
    }

    const showEmptyState = !loading && items.length === 0 && generatingItems.length === 0;

    if (showEmptyState) {
      return (
        <div className="mt-12 bg-white border border-gray-200 rounded-2xl p-10 text-center">
          <p className="text-gray-700 font-semibold">
            Este producto aún no tiene creativos.
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Cuando generes creativos, aparecerán aquí.
          </p>
        </div>
      );
    }

    return (
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {generatingItems.map((item) => (
          <GeneratingPlaceholderCard key={item.id} item={item} />
        ))}
        {items.map((item, index) => (
          <CreativeCard
            key={item.id != null ? String(item.id) : item.image_url}
            item={item}
            index={index + generatingItems.length}
            onClick={() => {
              console.log("Creative clicked:", item.name || item.product_name);
            }}
            onView={handleViewCreative}
            onEdit={handleEditCreative}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={`min-h-screen bg-[#F5F6FA] font-['Plus_Jakarta_Sans'] ${activeTab !== "scripts" ? "max-w-[90%] mx-auto p-8" : ""}`}>
      {/* Tab switcher */}
      <div className={`flex items-center bg-gray-200 rounded-full p-1 w-fit mb-10 ${activeTab === "scripts" ? "mx-8 mt-8" : ""}`}>
        <button
          onClick={() => setActiveTab("images")}
          className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            activeTab === "images"
              ? "bg-white text-[#5050EA] shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Imágenes
        </button>
        <button
          onClick={() => setActiveTab("scripts")}
          className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
            activeTab === "scripts"
              ? "bg-white text-[#5050EA] shadow-sm"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Guiones
        </button>
      </div>

      {/* Images tab — always mounted, hidden when inactive to preserve grid state */}
      <div className={activeTab !== "images" ? "hidden" : ""}>
        <FunnelCreativesHeader
          title={truncateProductName(product?.name?.trim() || "Producto")}
          imagesCount={items.length}
          thumbnailUrl={product?.image_url ?? items[0]?.image_url}
          onBack={() => navigate("/ads")}
          onCreate={() => setIsCreateModalOpen(true)}
        />
        {renderCreativesContent()}

        <CreateAdWithIAModal
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          initialStep={2}
          initialProduct={product}
        />

        <ViewCreativeModal
          open={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          creatives={items}
          initialIndex={viewModalIndex}
          language={language as Language}
          initialEditMode={viewModalEditMode}
          onCreativeUpdated={(id, newImageUrl) => {
            setItems((prev) =>
              prev.map((item) =>
                item.id === id ? { ...item, image_url: newImageUrl } : item
              )
            );
          }}
        />
      </div>

      {activeTab === "scripts" && <HyphensTabContent productId={productId} />}
    </div>
  );
}
